import { motion } from "framer-motion";
import type { TruthAuditData, TruthAuditSeverity } from "../types";

/**
 * Beyond Sure — truth-audit / fact-check block.
 *
 * This is the trust feature — the assistant flagged what's IN the
 * policy, what's MISSING, what to watch out for. Each finding is
 * rendered with a severity-coded icon (ok / warn / block) so the
 * user's eye lands on problems first.
 *
 * Visually distinct from RecommendationCard so users learn to
 * recognize "this is the audit lens, not a recommendation."
 */

interface TruthAuditBlockProps {
  data: TruthAuditData;
}

export function TruthAuditBlock({ data }: TruthAuditBlockProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22 }}
      data-testid="message-truth-audit"
      className={[
        "rounded-[14px] overflow-hidden",
        "bg-[rgb(255_255_255_/_0.92)] backdrop-blur-sm",
        "border border-[var(--color-ink-08)]",
        "shadow-[0_6px_22px_-14px_rgba(20,75,51,0.28)]",
      ].join(" ")}
    >
      {/* Header strip — branded, calm */}
      <div
        className={[
          "flex items-center gap-2 px-4 sm:px-5 py-2.5",
          "border-b border-[var(--color-ink-08)]",
          "bg-[linear-gradient(90deg,var(--color-brand-deep),var(--color-brand))]",
        ].join(" ")}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
          <path
            d="M12 2l9 4v6c0 5-4 9-9 10-5-1-9-5-9-10V6l9-4z"
            stroke="var(--color-paper)"
            strokeWidth="1.8"
            strokeLinejoin="round"
          />
          <path
            d="M9 12l2 2 4-4"
            stroke="var(--color-paper)"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <p className="bsure-caps text-[10.5px] font-semibold tracking-[0.16em] text-[var(--color-paper)]">
          Truth-audit · {data.policyName}
        </p>
      </div>

      <ul className="flex flex-col">
        {data.findings.map((f, i) => (
          <li
            key={`${f.label}-${i}`}
            className={[
              "flex items-start gap-3 px-4 sm:px-5 py-3",
              i < data.findings.length - 1
                ? "border-b border-[var(--color-ink-08)]"
                : "",
            ].join(" ")}
          >
            <SeverityIcon severity={f.severity} />
            <div className="flex-1 min-w-0">
              <p className="text-[14px] font-semibold text-[var(--color-ink)]">
                {f.label}
              </p>
              <p className="text-[12.5px] leading-[1.5] text-[var(--color-ink-60)] mt-0.5">
                {f.detail}
              </p>
            </div>
            <SeverityChip severity={f.severity} />
          </li>
        ))}
      </ul>
    </motion.div>
  );
}

function SeverityIcon({ severity }: { severity: TruthAuditSeverity }) {
  const palette = palettesFor(severity);
  return (
    <span
      className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full mt-0.5"
      style={{ backgroundColor: palette.bg }}
      aria-hidden
    >
      {severity === "ok" ? (
        <svg width="11" height="11" viewBox="0 0 16 16" fill="none">
          <path
            d="M3 8.5l3 3 7-7"
            stroke={palette.ink}
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : severity === "warn" ? (
        <svg width="11" height="11" viewBox="0 0 16 16" fill="none">
          <path
            d="M8 4v5M8 11.4v0.6"
            stroke={palette.ink}
            strokeWidth="2.2"
            strokeLinecap="round"
          />
        </svg>
      ) : (
        <svg width="11" height="11" viewBox="0 0 16 16" fill="none">
          <path
            d="M4 4l8 8M12 4l-8 8"
            stroke={palette.ink}
            strokeWidth="2.2"
            strokeLinecap="round"
          />
        </svg>
      )}
    </span>
  );
}

function SeverityChip({ severity }: { severity: TruthAuditSeverity }) {
  const palette = palettesFor(severity);
  const label =
    severity === "ok" ? "OK" : severity === "warn" ? "Watch" : "Issue";
  return (
    <span
      className="inline-flex shrink-0 h-5 px-2 items-center rounded-full text-[9.5px] font-semibold uppercase tracking-[0.12em]"
      style={{
        backgroundColor: palette.bg,
        color: palette.ink,
      }}
    >
      {label}
    </span>
  );
}

function palettesFor(severity: TruthAuditSeverity): {
  bg: string;
  ink: string;
} {
  switch (severity) {
    case "ok":
      return { bg: "var(--color-brand-soft)", ink: "var(--color-brand-deep)" };
    case "warn":
      return { bg: "rgb(255 211 0 / 0.18)", ink: "#7a5d00" };
    case "block":
      return { bg: "rgb(214 64 64 / 0.12)", ink: "#9a3a3a" };
  }
}

export default TruthAuditBlock;
